import Backgrounds from '@/components/ToolPopovers/Background';

import {
  MODAL_TYPES,
  PER_PAGE_10,
  SAVED_BACKGROUND_TYPE
} from '@/common/constants';

import {
  useAppCommon,
  useGenerateBackground,
  usePopoverCreationTool,
  usePrintBackgroundMenu
} from '@/hooks';

import { cloneDeep } from 'lodash';

import {
  isHalfSheet as isSheetHalfSheet,
  getBackgroundType,
  getBackgroundPageType,
  isFullBackground,
  isHalfRight
} from '@/common/utils';

export default {
  components: {
    Backgrounds
  },
  setup() {
    const { setToolNameSelected } = usePopoverCreationTool();

    const {
      currentSheet,
      currentThemeId,
      toggleModal,
      getBackgroundTypeData,
      getBackgroundData,
      getBackgroundCategories
    } = usePrintBackgroundMenu();
    const { setNotification } = useAppCommon();
    const { getGeneratedBackground } = useGenerateBackground();

    return {
      setToolNameSelected,
      currentSheet,
      currentThemeId,
      toggleModal,
      getBackgroundTypeData,
      getBackgroundData,
      setNotification,
      getBackgroundCategories,
      getGeneratedBackground
    };
  },
  data() {
    return {
      backgroundTypes: {},
      backgrounds: [],
      noBackgroundLength: 4,
      selectedType: { sub: '' },
      selectedPageType: {},
      appliedBackground: {},
      isProduction: process.env.VUE_APP_PRODUCTION,
      isLoadingCategory: true,
      perPages: PER_PAGE_10,
      currentPage: 1,
      isLoadingBackgrounds: false,
      currentPageSavedBackground: 1
    };
  },
  computed: {
    isHalfSheet() {
      return isSheetHalfSheet(this.currentSheet);
    }
  },
  mounted() {
    this.initData();
  },
  methods: {
    /**
     * Init data when loaded
     */
    async initData() {
      this.backgroundTypes = await this.getBackgroundTypeData();
      // Update for PP-2407
      if (this.isProduction) {
        this.selectedType = {
          value: this.backgroundTypes.CATEGORY.id,
          sub: this.backgroundTypes.CATEGORY.value[0].id
        };
      } else {
        this.selectedType = getBackgroundType(
          this.appliedBackground,
          this.backgroundTypes,
          this.currentThemeId
        );
      }
      if (!this.selectedType.sub) {
        this.selectedType.sub = this.backgroundTypes.THEME.value[0].id;
        const notification = {
          isShow: true,
          type: 'warning',
          title: 'Warning',
          text: 'Please select a theme for this book'
        };
        this.setNotification({ notification });
      }
      this.selectedPageType = getBackgroundPageType(
        this.appliedBackground,
        this.isHalfSheet
      );

      this.getBackgrounds();
    },
    /**
     * Get background from API
     */
    async getBackgrounds() {
      if (this.selectedType.sub === SAVED_BACKGROUND_TYPE.value) {
        // This is a requirement for PP-3032
        // if (this.selectedPageType.id === BACKGROUND_PAGE_TYPE.GENERAL.id) {
        //   this.selectedPageType = getBackgroundPageType(
        //     this.appliedBackground,
        //     true
        //   );
        // }
        this.backgrounds = await this.getGeneratedBackground(
          this.selectedPageType.value,
          this.currentPageSavedBackground,
          false
        );
        if (this.backgrounds.length >= this.perPages) {
          this.isLoadingBackgrounds = true;
        }
        return;
      }
      this.backgrounds = await this.getBackgroundData(
        this.selectedType.value,
        this.selectedType.sub,
        this.selectedPageType.value
      );
    },
    /**
     * Event fire when choose background type
     *
     * @param {Object}  data  data of chosen background type
     */
    onChangeType(data) {
      this.selectedType = data;
      this.currentPageSavedBackground = 1;

      this.getBackgrounds();
    },
    /**
     * Event fire when choose background page type
     *
     * @param {Object}  data  data of chosen background page type
     */
    onChangePageType(data) {
      this.selectedPageType = data;
      this.currentPageSavedBackground = 1;

      this.getBackgrounds();
    },
    /**
     * Trigger hooks to set tool name is empty and then close popover when click Cancel button
     */
    onClose() {
      this.setToolNameSelected('');
    },
    /**
     * Trigger mutation to set Background
     *
     * @param {Object}  background  selected background
     */
    onApplyBackground(background) {
      const { pageType: selectedPageType } = background;

      const isSinglePageType = !isFullBackground({
        pageType: selectedPageType
      });

      if (!this.isHalfSheet && isSinglePageType) {
        this.toggleModal({
          isOpenModal: true,
          modalData: {
            type: MODAL_TYPES.BACKGROUND_SELECT_PAGE,
            props: {
              numberPageLeft: this.currentSheet.pageLeftName,
              numberPageRight: this.currentSheet.pageRightName,
              background: {
                ...cloneDeep(background),
                opacity: 1
              }
            }
          }
        });

        this.onClose();

        return;
      }

      const isLeft = !isHalfRight(this.currentSheet);

      this.$root.$emit('printAddBackground', {
        background: {
          ...cloneDeep(background),
          opacity: 1
        },
        isLeft
      });

      this.onClose();
    },
    async onLoadMoreCategory() {
      if (this.currentPage > 0) {
        this.currentPage++;
        const bgCategories = await this.getBackgroundCategories(
          this.currentPage,
          true
        );
        if (bgCategories.length < this.perPages) {
          this.isLoadingCategory = false;
          this.currentPage = -1;
        }
        this.backgroundTypes.CATEGORY.value = [
          ...this.backgroundTypes.CATEGORY.value,
          ...bgCategories
        ];
        this.backgroundTypes.CATEGORY.value = [
          this.backgroundTypes.CATEGORY.value[0],
          ...this.backgroundTypes.CATEGORY.value
            .slice(1)
            .sort((item1, item2) => item1.name.localeCompare(item2.name))
        ];
      }
    },
    async onLoadMoreBackgrounds() {
      if (this.currentPageSavedBackground < 0) {
        this.isLoadingBackgrounds = false;
        return;
      }

      this.currentPageSavedBackground++;
      const savedBackground = await this.getGeneratedBackground(
        this.selectedPageType.value,
        this.currentPageSavedBackground,
        true
      );
      if (savedBackground.length < this.perPages) {
        this.isLoadingBackgrounds = false;
        this.currentPageSavedBackground = -1;
      }
      this.backgrounds = [...this.backgrounds, ...savedBackground];
    }
  }
};
